import React, { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { observe } from 'mobx'
import { SlideDown } from 'react-slidedown'
import { byId } from 'core/utils'
import { Avatar, Icon, T, FreeChatWindowRing } from 'components/misc'
import AnimatedSwap from './AnimatedSwap'
import { MODES } from 'core/constants'
import { useEventListener } from 'core/hooks'
import { debounce } from 'lodash'
import { ccpProvider } from 'core/CcpProvider'
import request from 'core/request'
import Conversation from '../../models/Conversation'
import VIPBadge from 'components/ChatHeader/VIPBadge'

const maxShown = () => {
  const width = document.documentElement.clientWidth
  return window.innerWidth > 480 ? 4 : Math.max(4, ((width - 37) / 58) - 1)
}

const ExtraButton = ({ count, toggleExtra, showExtra }) => {
  return (<div className="conversation-picker-item hidden-conversations" key={999} onClick={toggleExtra}>
    <div className="hidden-conversations-circle">
      <span>{showExtra ? <>&#10005;</> : `+${count}`}</span>
    </div>
  </div>)
}

const ConversationPicker = ({ store, actions, isOpen, options: { mode, features }, toggleOpen }) => {
  const [showExtra, setShowExtra] = useState(false)
  const [maxDisplay, setMaxDisplay] = useState(maxShown())

  const onWindowClick = e => {
    if (showExtra && !document.querySelector('.conversation-picker').contains(e.target)) {
      setShowExtra(false)
    }
  }
  useEventListener('click', onWindowClick, [], window)
  useEventListener('resize', () => setMaxDisplay(maxShown()), [], window)

  useEffect(() => {
    observe(store.conversations, change => {
      if (change.addedCount > 0 && !isOpen) {
        toggleOpen()
      }
    })
  }, [])

  const fetchConversations = debounce(() => {
    loadMoreConversations().then(response => {
      response.conversations.forEach(conversation => {
        if (store.conversations.some(conv => conversation.conversationUid === conv.conversationUid)) {
          return
        }

        conversation = new Conversation(conversation) //otherwise avatar fails as it cannot get the otherUserProfile relation
        store.conversations.push(conversation)
        actions.chat.updateConversationCache(conversation)
      })
   })
  }, 500)

  const onScroll = e => {
    const percentScroll = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)
    if (percentScroll < 100) {
      setTimeout(() => {
        fetchConversations()
      }, 2000)
    }
  }

  const loadMoreConversations = async() => {
    let lastConvo = store.conversations[store.conversations.length-1]

    if (!lastConvo) {
      return
    }

    let uuid = lastConvo.conversationUid
    let requestData = {userUid: CONFIG.userUid, lastReceivedUid: uuid}

    const response = await request.get(ccpProvider.api.widget.endpoints.WIDGET_GET_CONVERSATIONS, requestData, [])
    return response
  }

  const openConversation = conv => {
    actions.chat.setUserConversation(conv.conversationUid)
    actions.chat.readConversation(conv)
    conv.unreadByMeCount = 0
    setShowExtra(false)
  }

  const toggleExtra = () => setShowExtra(!showExtra)

  const conversations = store.visibleConversations
  const conversationUid = store.conversation.conversationUid

  const active = conversations.find(byId(conversationUid))
  const others = conversations.filter(c => c !== active)
  const shown  = [active].concat(others.slice(0, maxDisplay - 1))
  const hidden = others.slice(maxDisplay - 1)
  const unread = conversations.reduce((total, c) => total + c.unreadByMeCount, 0)

  const PickerItem = c => {
    const id = c.conversationUid
    const isActive = id === conversationUid
    const onClick = () => isOpen && openConversation(c)
    const shouldShowVipBadge = c.otherUserProfile.isFeatured && isOpen && features.featuredFunctionality || false;

    return (
      <div className={`conversation-picker-item ${isActive ? 'conversation-picker-item-active' : ''}`} key={id} data-id={id}>
        { c.unreadByMeCount > 0 && <span className="conversation-picker-unread">{c.unreadByMeCount}</span> }
        <Avatar isActive={isActive} user={c.otherUserProfile} onClick={onClick}/>
        {shouldShowVipBadge && <span><VIPBadge/></span>}
      </div>
    )
  }

  return (
    <div onScroll={onScroll} className={`conversation-picker conversation-picker-${showExtra ? 'show' : 'hide'}-extra`} onClick={isOpen ? undefined : toggleOpen}>
      { mode === MODES.VERTICAL && isOpen ? (
        <>
          <AnimatedSwap active={active} shown={[active]} renderItem={PickerItem} name="active"/>
          <AnimatedSwap active={active} shown={others} renderItem={PickerItem} name="main"/>
        </>
      ) : (
        <AnimatedSwap active={active} shown={shown} renderItem={PickerItem} name="main" extra={ hidden.length > 0 && (<ExtraButton count={hidden.length} showExtra={showExtra} toggleExtra={toggleExtra}/>) }/>
      )}
      { isOpen && hidden.length > 0 &&
        <SlideDown className="conversation-picker-extra">
          {showExtra ? hidden.map(PickerItem) : null}
        </SlideDown>
      }
      { !isOpen && <>
          <span className="conversation-picker-label">
            {conversations.length === 1 ? active.otherUserProfile.username : <>{conversations.length} <T>conversations</T></>}
          </span>
        <FreeChatWindowRing size="compact"/>
        <span className="widget-unread-notifier">{unread || ''}</span>
      </>
      }
      { mode !== MODES.VERTICAL && <Icon iconName="arrowDown" className={`m${isOpen ? 'in': 'ax'}imize`} onClick={isOpen ? toggleOpen : undefined}/> }
    </div>
  )
}

export default inject('store', 'actions')(observer(ConversationPicker))
